// ============================================================================
// OBS-01 — Sentry init para frontend React
//
// Política Zero-Exfiltration:
//   - maskAllText / maskAllInputs (Replay desactivado de todos modos por compliance)
//   - beforeSend hook: scrub JWTs, SHA-256, campos financieros
//   - sendDefaultPii: false
//   - Session Replay: COMPLETAMENTE OFF
// ============================================================================

import * as Sentry from "@sentry/react";

const SENSITIVE_KEYS = new Set([
  "password", "token", "authorization", "auth", "apikey", "api_key",
  "service_role_key", "anon_key", "jwt", "secret",
  "valor_total", "valor_unitario", "precio", "amount", "total",
  "subtotal", "monto",
  "sha256", "hash", "prev_hash", "row_hash", "dossier_root_hash", "signature",
]);

function scrub(value: unknown, depth = 0): unknown {
  if (depth > 6 || value == null) return value;
  if (typeof value === "string") {
    if (/^eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(value)) return "[REDACTED_JWT]";
    if (/^[a-f0-9]{64}$/i.test(value)) return "[REDACTED_HASH]";
    return value;
  }
  if (Array.isArray(value)) return value.map((v) => scrub(v, depth + 1));
  if (typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = SENSITIVE_KEYS.has(k.toLowerCase()) ? "[REDACTED]" : scrub(v, depth + 1);
    }
    return out;
  }
  return value;
}

// DSN público de Sentry (diseñado para ir embebido en el bundle del navegador).
// Fallback hardcodeado para evitar dependencia de build secrets.
const SENTRY_DSN_FALLBACK =
  "https://2d6310c2e1e2adc395f9ec55c4f15e63@o4511334043811840.ingest.us.sentry.io/4511334047875072";

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN_FRONTEND || SENTRY_DSN_FALLBACK;
  if (!dsn) {
    // En dev sin DSN no rompemos nada
    return;
  }
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    tracesSampleRate: 0.1,
    sendDefaultPii: false,
    // Session Replay COMPLETAMENTE OFF (decisión de compliance)
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    beforeSend(event) {
      if (event.request) event.request = scrub(event.request) as typeof event.request;
      if (event.extra) event.extra = scrub(event.extra) as typeof event.extra;
      if (event.contexts) event.contexts = scrub(event.contexts) as typeof event.contexts;
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((b) => ({
          ...b,
          data: b.data ? (scrub(b.data) as Record<string, unknown>) : b.data,
          message: b.message,
        }));
      }
      if (event.user) {
        event.user = { id: event.user.id };
      }
      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      // Bloquear breadcrumbs de console.log/info para evitar PII accidental
      if (breadcrumb.category === "console" && breadcrumb.level !== "error" && breadcrumb.level !== "warning") {
        return null;
      }
      return breadcrumb;
    },
  });
}

export { Sentry };
